import React, { useContext, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import API, { runGlobalApiErrorHandler } from '../../../../services/API'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import ConcurrentRoomLimitAlert from '../../../../components/Subscriptions/ConcurrentRoomLimitAlert'
import { isControlRoomLimit } from '../../../../helpers/controlRoomCapacity'
import { UserContext } from '../../../../App'

const AddCustomControlRoom = ({}) => {
  const [title, setTitle] = useState('')
  const [error, setError] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [duplicateError, setDuplicateError] = useState(false)
  // The 403 payload when the plan's concurrent control-room capacity is in
  // use.
  const [limitInfo, setLimitInfo] = useState(null)

  const { setSiteMode, setSelectedControlRoom } = useContext(SiteModeContext)
  const { user } = useContext(UserContext)
  const isCommander =
    !!user?.security?.isSiteAdmin || user?.employee?.cadAccess === 'Commander'

  // Starting a control room is a commander decision (enforced server-side).
  if (!isCommander) {
    return (
      <Alert severity="info" sx={{ borderRadius: 3 }}>
        Only commanders can create control rooms. Ask a commander to open one
        — you'll then be able to join it from the dashboard.
      </Alert>
    )
  }

  const handleSubmit = () => {
    setUploading(true)
    setError(false)
    setLimitInfo(null)

    // skipGlobalErrorHandler so the concurrent-room refusal is explained
    // here; every other failure still goes to the global handler, as before.
    API.post(
      '/commandcontrol/dashboard/start-control-room',
      { customTitle: title },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (isControlRoomLimit(res)) {
          setLimitInfo(res.data)
        } else if (res.data?.existingRoomID) {
          setDuplicateError(res.data.existingRoomID)
        } else if (res.status === 200) {
          setSelectedControlRoom(res.data)
          setSiteMode('eventControl')
        } else {
          runGlobalApiErrorHandler({ response: res })
        }
      })
      .catch((e) => {
        if (runGlobalApiErrorHandler(e)) return
        console.error(e)
        setError(true)
      })
      .finally(() => setUploading(false))
  }

  return (
    <Stack
      sx={{
        p: 1,
      }}
      gap={2}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack spacing={0.25}>
          <Typography variant="overline">Command & Control</Typography>

          <Typography variant="h5" fontWeight={900}>
            Create Control Room
          </Typography>

          <Typography variant="body2">
            Start a standalone operational control room.
          </Typography>
        </Stack>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.22)',
            boxShadow: '0 12px 30px rgba(0,0,0,0.22)',
          }}
        >
          <MeetingRoomIcon sx={{ fontSize: 30 }} color='contrast' />
        </Box>
      </Stack>

      <Stack spacing={2.5}>
        <Alert icon={<InfoOutlinedIcon />} severity="error" variant="filled">
          This will create a custom control room that is <strong><u>NOT</u></strong> associated with an
          event.
        </Alert>

        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: 3,
            }}
          >
            Unable to create the control room. Please try again.
          </Alert>
        )}

        {limitInfo && <ConcurrentRoomLimitAlert limitInfo={limitInfo} />}

        {duplicateError && (
          <Alert
            icon={<WarningAmberIcon />}
            severity="warning"
            sx={{
              borderRadius: 3,
            }}
          >
            A control room with this name may already exist.
          </Alert>
        )}

        <TextField
          label="Control room name"
          placeholder="e.g. Norfolk PTS Desk"
          value={title}
          onChange={(e) => {
            duplicateError && setDuplicateError(false)
            setError(false)
            setLimitInfo(null)
            setTitle(e.target.value)
          }}
          fullWidth
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MeetingRoomIcon fontSize="small" />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 3,
              backgroundColor: '#FFFFFF',
            },
          }}
        />

        <Divider />

        <Button
          disabled={!!duplicateError || uploading || !title.trim()}
          onClick={handleSubmit}
          variant="contained"
          endIcon={
            uploading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <RocketLaunchIcon />
            )
          }
          sx={{
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 900,
            px: 3,
            backgroundColor: '#239AA4',
            boxShadow: '0 12px 28px rgba(35,154,164,0.28)',
            '&:hover': {
              backgroundColor: '#1E7F88',
              boxShadow: '0 16px 34px rgba(35,154,164,0.34)',
            },
          }}
        >
          Create & Launch
        </Button>
      </Stack>
    </Stack>
  )
}

export default AddCustomControlRoom
